import { format, formatDistanceToNow, isWithinInterval, subDays } from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";

/**
 * Gets the user's local timezone
 */
function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/**
 * Formats a date for people-friendly display with timezone awareness
 * @param dateString - ISO date string, Date object, or date-only string (YYYY-MM-DD)
 * @param options - Formatting options
 * @returns Formatted date string
 */
export function formatPeopleDate(
  dateString: string | Date, 
  options: { 
    relative?: boolean; 
    includeTime?: boolean;
    locale?: string;
  } = {}
): string {
  const { relative = false, includeTime = true } = options;
  
  if (!dateString) return '';
  
  const userTz = getUserTimezone();
  let date: Date;
  
  // Handle different input formats
  if (typeof dateString === 'string') {
    // Check if it's a date-only string (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      // For date-only strings, don't apply timezone conversion
      date = new Date(dateString + 'T12:00:00');
      
      // For date-only, never show time and don't use relative formatting
      return format(date, 'd MMM yyyy');
    } else {
      // Full timestamp - convert to user's timezone
      date = toZonedTime(new Date(dateString), userTz);
    }
  } else {
    // Date object - convert to user's timezone
    date = toZonedTime(dateString, userTz);
  }
  
  // Check if date is within last 7 days for relative formatting
  const now = toZonedTime(new Date(), userTz);
  const isRecent = isWithinInterval(date, {
    start: subDays(now, 7),
    end: now
  });
  
  if (relative && isRecent) {
    return formatDistanceToNow(date, { addSuffix: true });
  }
  
  // Full date format with timezone: "11 Jul 2025 · 14:37" or "11 Jul 2025"
  const timeFormat = includeTime ? " · HH:mm" : "";
  return formatInTimeZone(date, userTz, `d MMM yyyy${timeFormat}`);
}

/**
 * Formats payment time using paid_at timestamp
 * @param payment - Payment object with date and paid_at
 * @param options - Formatting options
 * @returns Formatted date string
 */
export function formatPaymentTime(
  payment: { 
    date?: string; 
    paid_at?: string; 
  },
  options: { relative?: boolean } = {}
): string {
  const { relative = false } = options;
  
  // Always use paid_at if available, fallback to date
  const timestamp = payment.paid_at || payment.date;
  
  if (!timestamp) return '';
  
  return formatPeopleDate(timestamp, { relative, includeTime: true });
}

/**
 * Formats date with full ISO string for accessibility
 * @param dateString - ISO date string or Date object
 * @returns Object with display text and aria-label
 */
export function formatDateWithA11y(
  dateString: string | Date,
  options: { 
    relative?: boolean; 
    includeTime?: boolean;
  } = {}
) {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  const displayText = formatPeopleDate(dateString, options);
  const ariaLabel = date.toISOString();
  
  return {
    displayText,
    ariaLabel
  };
}

/**
 * Converts datetime-local input to consistent UTC timestamp
 * This ensures timezone-consistent handling for appointment scheduling
 * @param datetimeLocalValue - Value from datetime-local input (YYYY-MM-DDTHH:mm)
 * @returns ISO string in UTC
 */
export function convertDatetimeLocalToUTC(datetimeLocalValue: string): string {
  // datetime-local format: "2025-09-04T14:00"
  // We need to treat this as the user's local time and convert to UTC consistently
  const userTz = getUserTimezone();
  
  // Parse the datetime-local string components
  const [dateStr, timeStr] = datetimeLocalValue.split('T');
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  
  // Create date in user's timezone using date-fns-tz
  const localDate = new Date(year, month - 1, day, hour, minute);
  
  // Convert to UTC for consistent database storage/querying
  return localDate.toISOString();
}

/**
 * Converts UTC timestamp to datetime-local format for form inputs
 * @param utcTimestamp - ISO string in UTC
 * @returns datetime-local format string (YYYY-MM-DDTHH:mm)
 */
export function convertUTCToDatetimeLocal(utcTimestamp: string): string {
  const userTz = getUserTimezone();
  
  // Convert UTC to user's local timezone
  const localDate = toZonedTime(new Date(utcTimestamp), userTz);
  
  // Format for datetime-local input
  return format(localDate, "yyyy-MM-dd'T'HH:mm");
}